import React, { FormEventHandler, useState } from "react";
import { Button, Dropdown, LoadingErrorState } from "../components";
import { toast } from "react-toastify";
import { Save } from "lucide-react";
import { client } from "../helpers";
import { useAuth, useTranslations } from "../contexts";
import { InferRequestType } from "hono/client";
import { AsyncStatus } from "../helpers/asyncStatus";

type ClientUserPatch = (typeof client.api)["users"]["me"]["$patch"];
type ClientSitePatchRequest = InferRequestType<ClientUserPatch>["json"];

const DangerZone: React.FC = () => {
  const auth = useAuth();
  const i18n = useTranslations();
  const [status, setStatus] = useState<AsyncStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const isDeleteConfirmed = () =>
    window.prompt(
      i18n[
        "Are you sure you want to delete your account? To confirm, type DELETE."
      ]
    ) === i18n.confirmDeleteWord;

  const deleteUser = async () => {
    if (status === "loading") return;
    setStatus("loading");
    setError(null);
    try {
      const response = await client.api.users.$delete({
        header: { authorization: `Bearer ${auth.token}` },
      });
      if (response.status === 200) {
        setStatus("success");
        window.alert(i18n["Your account has been deleted"]);
        window.location.reload();
        return;
      }
      setStatus("error");
      setError(i18n["Something went wrong"]);
    } catch (deleteError) {
      console.error(deleteError);
      setStatus("error");
      setError(i18n["Something went wrong"]);
    }
  };

  return (
    <div
      className="mt-8 p-3 border rounded border-red-600"
      aria-busy={status === "loading"}
    >
      <h2 className="text-xl text-red-600 font-bold mb-4">
        {i18n.dangerZone}
      </h2>
      <Button
        id="delete-account-btn"
        variant="destructive"
        loading={status === "loading"}
        onClick={(event) => {
          if (status === "loading") {
            event.preventDefault();
            return;
          }
          if (isDeleteConfirmed()) {
            deleteUser();
          } else {
            event.preventDefault();
          }
        }}
      >
        {i18n["delete your account"]}
      </Button>
      <p className="mt-2 text-gray-600">
        {i18n["This action cannot be reverted or undone"]}
      </p>
      {error && (
        <LoadingErrorState
          className="mt-3"
          message={error}
          retryLabel={i18n["Retry"]}
          onRetry={() => {
            if (isDeleteConfirmed()) {
              deleteUser();
            }
          }}
        />
      )}
    </div>
  );
};

export const Language: React.FC<{
  onChange: (params: Pick<ClientSitePatchRequest, "language">) => void;
  disabled?: boolean;
}> = (props) => {
  const i18n = useTranslations();
  const auth = useAuth();
  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">{i18n.general}</h2>
      <Dropdown
        options={[
          { label: `🇬🇧 ${i18n.English}`, value: "en" },
          { label: `🇫🇷 ${i18n.French}`, value: "fr" },
        ]}
        placeholder={i18n.language}
        defaultValue={auth.user?.language}
        disabled={props.disabled}
        onChange={(event) => {
          i18n.setLanguage(event.target.value);
          props.onChange({ language: event.target.value });
        }}
      />
    </div>
  );
};

export const SettingsPage: React.FC = () => {
  const i18n = useTranslations();
  const auth = useAuth();
  const [form, setForm] = useState<ClientSitePatchRequest>();
  const [status, setStatus] = useState<AsyncStatus>("idle");
  const isSaving = status === "loading";
  const [submitError, setSubmitError] = useState<string | null>(null);

  const saveSettings = async (payload: ClientSitePatchRequest) => {
    if (isSaving) return;
    setStatus("loading");
    setSubmitError(null);
    try {
      const results = await (
        await client.api.users.me.$patch({
          header: { authorization: `Bearer ${auth.token}` },
          json: payload,
        })
      ).json();
      if (results.success) {
        setStatus("success");
        toast.success(i18n["Your settings have been updated"]);
        auth.setUser(results.data.user);
      } else {
        setStatus("error");
        setSubmitError(i18n["Something went wrong"]);
        toast.error(i18n["Something went wrong"]);
      }
    } catch (submitError) {
      console.error(submitError);
      setStatus("error");
      setSubmitError(i18n["Something went wrong"]);
      toast.error(i18n["Something went wrong"]);
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (!form || isSaving) return;
    await saveSettings(form);
  };

  return (
    <div
      id="settings-page"
      className="max-w-screen-md max-md:p-5 m-auto"
      aria-busy={isSaving}
    >
      <form onSubmit={handleSubmit}>
        <h1 className="text-2xl font-bold mb-6 flex">
          <p className="grow">{i18n.settings}</p>

          <Button loading={isSaving} type="submit" disabled={!form}>
            <div className="flex items-center gap-1">
              <Save className="mr-3 h-4 w-4" />
              {i18n.save}
            </div>
          </Button>
        </h1>
        <Language
          disabled={isSaving}
          onChange={(params) => setForm({ ...form, ...params })}
        />
        {submitError && (
          <LoadingErrorState
            className="mt-4"
            message={submitError}
            retryLabel={i18n["Retry"]}
            onRetry={() => {
              if (form) {
                saveSettings(form);
              }
            }}
          />
        )}
        <DangerZone />
      </form>
    </div>
  );
};
